// Number / percentage formatting shared across features.

/** Strip a trailing `.0` from a fixed-1-decimal string ("42.0" → "42"). */
function trimDecimalZero(oneDecimal: string): string {
  return oneDecimal.endsWith('.0') ? oneDecimal.slice(0, -2) : oneDecimal;
}

/**
 * Format a 0..1 ratio as a percentage with one decimal, dropping a `.0` tail.
 * `0.425` → "42.5%", `0.42` → "42%", `0.003` → "0.3%".
 */
export function formatPercent(value: number): string {
  return `${trimDecimalZero((value * 100).toFixed(1))}%`;
}

/**
 * Format an absolute count with locale grouping. `1234` → "1,234".
 *
 * Deliberately PINNED to `en-US` while `formatCurrency` below follows the
 * viewer's locale (localized-pricing decision 3). Indian grouping only diverges
 * from Western at ≥ 100,000, and every count this renders is below that — the
 * largest plan limit is `aggregateDailyWarmup: 20,000` — so `en-IN` and `en-US`
 * are byte-identical here and moving it would buy nothing.
 * **Revisit if** a tier ever ships ≥ 100,000 daily warmup: a card would then show
 * `100,000` next to `₹1,19,000`, mixing two grouping conventions on one line.
 */
export function formatNumber(value: number): string {
  return value.toLocaleString(FALLBACK_LOCALE);
}

/** Format a rate to two decimals. `1.5` → "1.50". */
export function formatRate(value: number): string {
  return value.toFixed(2);
}

/**
 * Locale used when the platform can't supply one (jsdom/SSR) and as the retry
 * when `navigator.language` turns out to be malformed.
 */
const FALLBACK_LOCALE = 'en-US';

/** The viewer's own locale, or the pinned fallback outside a browser. */
function viewerLocale(): string {
  return typeof navigator !== 'undefined' && navigator.language
    ? navigator.language
    : FALLBACK_LOCALE;
}

/**
 * Format a monetary amount in its currency. `29` + `"USD"` → "$29.00". Falls
 * back to a plain `<code> <amount>` render if the currency code is unknown to
 * `Intl` so a bad/absent code never throws at the render boundary.
 *
 * Formats in the VIEWER's locale by default (localized pricing): Paddle prices
 * an Indian visitor in INR, and Indian digit grouping diverges from Western at
 * ≥ 100,000 (`14,31,060` vs `1,431,060`) — which the yearly Scale Plus tiers
 * cross. A pinned `en-US` would render a rupee amount with Western grouping.
 * Amounts are unrounded FX output, so decimals are LEFT ALONE: `Intl` defaults
 * give 2 dp for USD/INR and 0 dp for JPY, which is exactly what we want. Never
 * force `maximumFractionDigits: 0` here — it hides real money.
 */
export function formatCurrency(
  amount: number,
  currency = 'USD',
  locale = viewerLocale(),
): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
  } catch {
    // A malformed `navigator.language` must not cost every price on the page its
    // symbol — retry on the pinned locale before giving up on `Intl` entirely.
    try {
      return new Intl.NumberFormat(FALLBACK_LOCALE, { style: 'currency', currency }).format(
        amount,
      );
    } catch {
      return `${currency} ${amount.toFixed(2)}`;
    }
  }
}

/**
 * Format a percentage-point delta as an absolute one-decimal value, dropping a
 * `.0` tail. The sign is expected to be conveyed separately (e.g. an arrow).
 * `12` → "12%", `-3.2` → "3.2%", `0` → "0%".
 */
export function formatDelta(delta: number): string {
  return `${trimDecimalZero(Math.abs(delta).toFixed(1))}%`;
}
